import * as Sentry from "@sentry/react";
import cookie from "cookie";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Provider } from 'react-redux'
import { SnackbarProvider } from "notistack";
import ProtectedCompanyPage from './components/ProtectedCompanyPage'
import ProtectedTalentPage from "./components/ProtectedTalentPage"
import ProtectedSuperUserPage from "./components/ProtectedSuperUserPage"
import Login from "./pages/Login";
import CompanyUserRegister from './pages/CompanyUserRegister'
import ResetPassword from './pages/ResetPassword'
import ResetPasswordConfirm from './pages/ResetPasswordConfirm'
import CompanyHome from "./pages/CompanyHome"
import TalentList from "./pages/talent/TalentList";
import TalentIndex from "./pages/talent/TalentIndex";
import TalentCSV from "./pages/talent/TalentCSV";
import TalentHomePage from "./pages/talent_home/Index"
import TalentNews from "./pages/talent_home/NewsDetail"
import RegisterComplete from "./pages/talent_view/RegisterComplete"
import TermsOfServices from "./pages/talent_view/TermsOfServices"
import Privacy from "./pages/talent_view/Privacy"
import Mypage from "./pages/talent_mypage/MyPageIndex"
import ChangePassword from "./pages/talent_mypage/components/ChangePassword"
import ChangePasswordComplete from "./pages/talent_mypage/components/ChangePasswordComplete"
import CompanyChat from "./pages/company_chat/Chat"
import CompanyAction from "./pages/action/Index"
import CompanyActionEdit from "./pages/action/ActionDetail"
import '../assets/index.css'
import TalentChat from "./pages/talent_chat/Chat"

import { OpenAPI } from "./api";
import store from "./store"
import StaffList from "./pages/staff/StaffList";
import ContentCreate from "./pages/content/ContentCreate";
import CompanyContent from "./pages/content/CompanyContent";
import ContentRankingList from "./pages/content/ContentRankingList";
import CompanyNewsEdit from "./pages/content/ContentCreate";
import CompanyNewsDetail from "./pages/content/NewsDetail";
import NotificationSetting from "./pages/content/NotificationSetting";
import JobManagement from "./pages/companyJob/JobManagement";
import NewJobCreation from "./pages/companyJob/NewJobCreation";
import CompanyJobEdit from "./pages/companyJob/NewJobCreation";
import CompanyJobDetail from "./pages/companyJob/JobDetail";
import CompanyJobPreview from "./pages/companyJob/JobPreview";
import CompanyJobCSV from "./pages/companyJob/JobCSV";
import CompanyJobPreviewTemp from "./pages/companyJob/JobPreviewTemp";
import TalentJob from "./pages/talent_job/TalentJob";
import TalentJobDetail from "./pages/talent_job/TalentJobDetail";
import CompanyHistory from "./pages/company_history/OperationHistory";
import SuperUser from "./pages/super_user/SuperUser";
import SuperUserCreation from "./pages/super_user/UserCreation";
import SuperUserDetail from "./pages/super_user/UserDetail";
import SuperUserEdit from "./pages/super_user/UserEdit";
import CompanySelection from "./pages/CompanySelection";
import CustomerCompany from "./pages/customer_company/CustomerCompany";
import CustomerCompanyCreation from "./pages/customer_company/CompanyCreation";
import CustomerCompanyDetail from "./pages/customer_company/CompanyDetail";
import CustomerCompanyEdit from "./pages/customer_company/CompanyEdit";
import DepartmentRegistration from "./pages/customer_company/DepartmentRegistration";
import PositionRegistration from "./pages/customer_company/PositionRegistration";
import SuperUserLogin from "./pages/LoginSuperUser";
import DropdownBox from "./components/DropdownBox";
import ScrollToTop from "./components/ScrollToTop";
import TalentRegistration from "./pages/talent_view/TalentRegistration";
import BulkMessageList from "./pages/talent/BulkMessageList";
import "../js/utils/i18n";
import TagsManagement from "./pages/talent/TagsManagement";
import RegistrationLinkManagement from "./pages/talent/RegistrationLinkManagement";
import TalentConfirmRegistration from "./pages/TalentConfirmRegistration";

OpenAPI.interceptors.request.use((request) => {
  if (!request.headers) {
    request.headers = {};
  }

  const csrftoken = document.querySelector<HTMLInputElement>(
    '[name=csrfmiddlewaretoken]'
  )?.value;

  if (csrftoken) {
    request.headers["X-CSRFTOKEN"] = csrftoken;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      request.headers["X-Timezone"] = timezone;
    }
  } catch (e) {
    console.error("Get timezone failed:", e);
  }

  return request;
});

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      autoHideDuration={7000}
    >
      <Provider store={store}>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="company/login" element={<Login view="company" />} />
            <Route path="company/register" element={<CompanyUserRegister/>} />
            <Route path="company/reset_password" element={<ResetPassword view="company" />} />
            <Route path="company/reset_password_confirm" element={<ResetPasswordConfirm view="company" />} />
            <Route path="company" element={<ProtectedCompanyPage><CompanyHome /></ProtectedCompanyPage>} />

            {/*Manager Talent*/}
            <Route path="company/talent" element={<ProtectedCompanyPage><TalentList /></ProtectedCompanyPage>} />
            <Route path="company/talent/register" element={<ProtectedCompanyPage><TalentIndex /></ProtectedCompanyPage>} />
            <Route path="company/talent/import" element={<ProtectedCompanyPage><TalentCSV /></ProtectedCompanyPage>} />
            <Route path="company/talent/bulk_list" element={<ProtectedCompanyPage><BulkMessageList /></ProtectedCompanyPage>} />
            <Route path="company/talent/tags" element={<ProtectedCompanyPage><TagsManagement /></ProtectedCompanyPage>} />
            <Route path="company/talent/register-link" element={<ProtectedCompanyPage><RegistrationLinkManagement /></ProtectedCompanyPage>} />

            {/*Company Chat*/}
            <Route path="company/message" element={<ProtectedCompanyPage><CompanyChat /></ProtectedCompanyPage>} />

            {/*Talent Chat*/}
            <Route path="talent/message" element={<ProtectedTalentPage><TalentChat /></ProtectedTalentPage>} />

            {/*Manager Actions*/}
            <Route path="company/action" element={<ProtectedCompanyPage><CompanyAction /></ProtectedCompanyPage>} />
            <Route path="company/action/edit/:id" element={<ProtectedCompanyPage><CompanyActionEdit /></ProtectedCompanyPage>} />

            {/*Talent view*/}
            <Route path="talent/login" element={<Login view="talent" />} />
            <Route path="talent/reset_password" element={<ResetPassword view="talent" />} />
            <Route path="talent/reset_password_confirm" element={<ResetPasswordConfirm view="talent" />} />
            
            <Route path="talent/registration" element={<TalentRegistration />} />
            <Route path="talent/register-complete" element={<RegisterComplete />} />
            <Route path="talent/terms-service" element={<TermsOfServices />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="talent/confirm-registration" element={<TalentConfirmRegistration view="talent" />} />

            {/*Talent Home*/}
            <Route path="talent" element={<ProtectedTalentPage><TalentHomePage /></ProtectedTalentPage>} />
            <Route path="talent/news/:id" element={<ProtectedTalentPage><TalentNews /></ProtectedTalentPage>} />

            <Route path="talent/job" element={<ProtectedTalentPage><TalentJob /></ProtectedTalentPage>} />
            <Route path="talent/job/detail/:id" element={<ProtectedTalentPage><TalentJobDetail /></ProtectedTalentPage>} />
            <Route path="talent/mypage" element={<ProtectedTalentPage><Mypage /></ProtectedTalentPage>} />
            <Route path="talent/mypage/change-password" element={<ProtectedTalentPage><ChangePassword /></ProtectedTalentPage>} />
            <Route path="talent/mypage/change-password-complete" element={<ProtectedTalentPage><ChangePasswordComplete /></ProtectedTalentPage>} />

            <Route path="company/staff" element={<ProtectedCompanyPage><StaffList /></ProtectedCompanyPage>} />
            <Route path="company/content" element={<ProtectedCompanyPage><CompanyContent /></ProtectedCompanyPage>} />
            <Route path="company/news/register" element={<ProtectedCompanyPage><ContentCreate /></ProtectedCompanyPage>} />
            <Route path="company/content-ranking" element={<ProtectedCompanyPage><ContentRankingList /></ProtectedCompanyPage>} />
            <Route path="company/news/edit/:id" element={<ProtectedCompanyPage><CompanyNewsEdit /></ProtectedCompanyPage>} />
            <Route path="company/news/detail/:id" element={<ProtectedCompanyPage><CompanyNewsDetail /></ProtectedCompanyPage>} />
            <Route path="company/notification-setting" element={<ProtectedCompanyPage><NotificationSetting /></ProtectedCompanyPage>} />
            <Route path="company/job" element={<ProtectedCompanyPage><JobManagement /></ProtectedCompanyPage>} />
            <Route path="company/job/register" element={<ProtectedCompanyPage><NewJobCreation /></ProtectedCompanyPage>} />
            <Route path="company/job/detail/:id" element={<ProtectedCompanyPage><CompanyJobDetail /></ProtectedCompanyPage>} />
            <Route path="company/job/edit/:id" element={<ProtectedCompanyPage><CompanyJobEdit /></ProtectedCompanyPage>} />
            <Route path="company/job/preview/:id" element={<ProtectedCompanyPage><CompanyJobPreview /></ProtectedCompanyPage>} />
            <Route path="company/job/import" element={<ProtectedCompanyPage><CompanyJobCSV /></ProtectedCompanyPage>} />
            <Route path="company/job/preview-temp/:id" element={<ProtectedCompanyPage><CompanyJobPreviewTemp /></ProtectedCompanyPage>} />
            <Route path="company/history" element={<ProtectedCompanyPage><CompanyHistory /></ProtectedCompanyPage>} />
            <Route path="company/selection" element={<ProtectedCompanyPage><CompanySelection view="company" /></ProtectedCompanyPage>} />

            {/*Manage Website*/}
            <Route path="control" element={<SuperUserLogin />} />
            <Route path="control/user" element={<ProtectedSuperUserPage><SuperUser /></ProtectedSuperUserPage>} />
            <Route path="control/user/register" element={<ProtectedSuperUserPage><SuperUserCreation /></ProtectedSuperUserPage>} />
            <Route path="control/user/detail/:id" element={<ProtectedSuperUserPage><SuperUserDetail /></ProtectedSuperUserPage>} />
            <Route path="control/user/edit/:id" element={<ProtectedSuperUserPage><SuperUserEdit /></ProtectedSuperUserPage>} />
            <Route path="control/company" element={<ProtectedSuperUserPage><CustomerCompany /></ProtectedSuperUserPage>} />
            <Route path="control/company/register" element={<ProtectedSuperUserPage><CustomerCompanyCreation /></ProtectedSuperUserPage>} />
            <Route path="control/company/detail/:id" element={<ProtectedSuperUserPage><CustomerCompanyDetail /></ProtectedSuperUserPage>} />
            <Route path="control/company/edit/:id" element={<ProtectedSuperUserPage><CustomerCompanyEdit /></ProtectedSuperUserPage>} />
            <Route path="control/department/register/:id" element={<ProtectedSuperUserPage><DepartmentRegistration /></ProtectedSuperUserPage>} />
            <Route path="control/position/register/:id" element={<ProtectedSuperUserPage><PositionRegistration /></ProtectedSuperUserPage>} />
            <Route path="control/selection" element={<ProtectedSuperUserPage><CompanySelection view="control" /></ProtectedSuperUserPage>} />

            {/*Common talent*/}
            <Route path="" element={<Login view="talent" />} />
            <Route path="/" element={<Login view="talent" />} />
          </Routes>
        </BrowserRouter>
      </Provider>
      <DropdownBox />
    </SnackbarProvider>
  </Sentry.ErrorBoundary>
);

export default App;
